import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Plus, ShoppingBag, Trash2, User, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import { CustomerTypeahead, type CustomerSearchResult } from '../Workshop/components/CustomerTypeahead';

interface SaleLine {
    key: string;
    description: string;
    quantity: string;
    unitPrice: string;
    costPrice: string;
}

const blankLine = (): SaleLine => ({
    key: crypto.randomUUID(),
    description: '',
    quantity: '1',
    unitPrice: '',
    costPrice: '',
});

/**
 * Counter sale — parts sold over the counter with no work attached.
 *
 * Implemented as a {{action_label}} with parts and no vehicle, then invoiced
 * through the normal path. That is deliberate: a counter sale is an ordinary
 * taxable sale, so it must reserve VAT, post COGS, hit the P&L and move
 * Safe-to-Spend exactly as any other sale does. Giving it its own invoice
 * pipeline would mean re-implementing all of that and getting one of them
 * subtly wrong — most likely VAT, most likely in the direction that costs the
 * user money.
 *
 * The customer is still required. It's a real sale to a real person, it may
 * need returning or warranting, and an invoice with nobody's name on it is not
 * much of a record.
 */
export const CounterSaleModal = ({
    isOpen,
    onClose,
    onCreated,
}: {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (jobId: string) => void;
}) => {
    const { activeOrgId, molded } = useVertical();
    const { jargon } = molded;

    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    // Set only when the operator picked someone out of the search. Passing the
    // id is stronger than relying on contact matching: it works for a customer
    // with no email and no phone on file, and it cannot be defeated by a typo.
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [lines, setLines] = useState<SaleLine[]>([blankLine()]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const updateLine = (key: string, patch: Partial<SaleLine>) =>
        setLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)));

    const total = lines.reduce(
        (sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0),
        0
    );

    const validLines = lines.filter(l => l.description.trim() && Number(l.unitPrice) > 0);
    const canSave = name.trim().length > 1 && validLines.length > 0 && !saving;

    const reset = () => {
        setName(''); setPhone(''); setEmail(''); setCustomerId(null);
        setLines([blankLine()]); setError(null);
    };

    const selectCustomer = (c: CustomerSearchResult) => {
        setCustomerId(c.id);
        setName(c.full_name);
        setPhone(c.phone ?? '');
        setEmail(c.email ?? '');
        setError(null);
    };

    // Clearing the link but keeping the typed details is the useful behaviour:
    // "same name, but this is a different person" is exactly when you'd unpick
    // the match, and blanking the boxes would make you type it all again.
    const clearCustomer = () => setCustomerId(null);

    const handleSave = async () => {
        if (!activeOrgId || !canSave) return;
        setSaving(true);
        setError(null);

        try {
            const { data, error: rpcError } = await supabase.rpc('fn_create_job_with_details', {
                p_org_id: activeOrgId,
                p_customer_id: customerId,
                p_customer_name: name.trim(),
                p_customer_phone: phone.trim(),
                p_customer_email: email.trim(),
                // No vehicle: nothing was worked on. The RPC treats blank
                // vehicle fields as "don't create one".
                p_vehicle_vrm: '',
                p_vehicle_make: '',
                p_vehicle_model: '',
                p_vehicle_colour: null,
                p_vehicle_mileage: null,
                p_vehicle_year: null,
                p_vehicle_engine: null,
                p_vehicle_fuel_type: null,
                p_job_description: 'Counter sale',
                p_line_items: validLines.map(l => ({
                    type: 'part',
                    description: l.description.trim(),
                    quantity: Number(l.quantity) || 1,
                    unitPrice: Number(l.unitPrice) || 0,
                    costPrice: Number(l.costPrice) || 0,
                })),
            });

            if (rpcError) throw rpcError;

            reset();
            onCreated(data as unknown as string);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Could not record the sale';
            setError(
                message.includes('FREE_TIER_LIMIT_REACHED')
                    ? 'You’ve used all 10 free jobs this month. Upgrade to Pro for unlimited.'
                    : message
            );
        } finally {
            setSaving(false);
        }
    };

    const field =
        'w-full bg-canvas border border-line rounded-lg px-3 py-2.5 text-sm text-fg placeholder-fg-4 outline-none focus:border-gold transition-colors';

    return createPortal(
        <div
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-scrim/70 backdrop-blur-sm px-0 sm:px-4 pt-[max(1rem,env(safe-area-inset-top))]"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="dialog"
            aria-modal="true"
            aria-label="New counter sale"
        >
            <div className="w-full sm:max-w-2xl bg-panel border border-line rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
                <header className="flex items-start justify-between gap-3 p-5 border-b border-line shrink-0">
                    <div className="flex items-center gap-3">
                        <span className="w-10 h-10 rounded-lg grid place-items-center bg-gold-fill/10 border border-gold/25">
                            <ShoppingBag className="w-5 h-5 text-gold" />
                        </span>
                        <div>
                            <h3 className="text-lg font-bold font-display text-fg">
                                Counter sale
                            </h3>
                            <p className="text-[13px] text-fg-3">
                                Parts sold with no work attached.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg text-fg-3 hover:text-fg hover:bg-card transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    <section className="space-y-3">
                        <h4 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-fg-4">
                            {jargon.client_label}
                        </h4>
                        {/* Search first, type second. Picking a saved record is
                            the only way to be certain this sale lands on the
                            existing customer rather than beside them. */}
                        {customerId ? (
                            <div className="flex items-center justify-between gap-3 rounded-lg border border-gold/25 bg-gold-fill/10 px-3 py-2.5">
                                <div className="flex items-center gap-2 min-w-0">
                                    <User className="w-4 h-4 shrink-0 text-gold" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-fg truncate">{name}</p>
                                        <p className="text-[12px] text-fg-3 truncate">
                                            {[phone, email].filter(Boolean).join(' · ') || 'No contact details on file'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={clearCustomer}
                                    className="shrink-0 text-[12px] font-medium text-fg-3 hover:text-fg underline underline-offset-2 transition-colors"
                                >
                                    Change
                                </button>
                            </div>
                        ) : (
                            <CustomerTypeahead
                                onSelect={selectCustomer}
                                placeholder={`Search saved ${jargon.client_label.toLowerCase()}s by name, full email or full phone…`}
                            />
                        )}

                        <input
                            className={field}
                            placeholder={`${jargon.client_label} name`}
                            value={name}
                            onChange={(e) => { setName(e.target.value); clearCustomer(); }}
                            autoFocus
                        />
                        <div className="grid sm:grid-cols-2 gap-3">
                            <input className={field} placeholder="Phone" value={phone}
                                onChange={(e) => { setPhone(e.target.value); clearCustomer(); }} />
                            <input className={field} placeholder="Email (for the invoice)" type="email"
                                value={email} onChange={(e) => { setEmail(e.target.value); clearCustomer(); }} />
                        </div>
                        <p className="text-[12px] text-fg-3">
                            {customerId
                                ? `Linked to a saved ${jargon.client_label.toLowerCase()} — this sale joins their history.`
                                : `Not found? Just type the details. An existing ${jargon.client_label.toLowerCase()} with the same phone or email is matched rather than duplicated.`}
                        </p>
                    </section>

                    <section className="space-y-3">
                        <h4 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-fg-4">
                            Parts sold
                        </h4>

                        {/* Labels rather than placeholders — a placeholder
                            disappears the moment you type, so on a row of four
                            numbers you lose track of which is Price and which is
                            Cost exactly when it matters. The delete button also
                            had a col-span-1 track (~25px at 320px); on a phone it
                            now gets its own full-width row alongside the line
                            total, which the row never showed at all. */}
                        {lines.map((line) => {
                            const lineTotal = (parseFloat(line.quantity) || 0) * (parseFloat(line.unitPrice) || 0);
                            return (
                            <div key={line.key} className="rounded-lg border border-line bg-card p-3 sm:p-0 sm:border-0 sm:bg-transparent sm:grid sm:grid-cols-12 sm:gap-2 sm:items-start min-w-0">
                                <label className="block mb-2 sm:mb-0 sm:col-span-4 min-w-0">
                                    <span className="sm:hidden block text-fd-3xs uppercase tracking-wider text-fg-3 font-display mb-1">Description</span>
                                    <input
                                        className={`${field} w-full`}
                                        value={line.description}
                                        onChange={(e) => updateLine(line.key, { description: e.target.value })}
                                    />
                                </label>

                                <div className="grid grid-cols-3 gap-2 sm:contents">
                                    <label className="block sm:col-span-2 min-w-0">
                                        <span className="sm:hidden block text-fd-3xs uppercase tracking-wider text-fg-3 font-display mb-1">Qty</span>
                                        <input
                                            className={`${field} w-full text-center`}
                                            inputMode="decimal"
                                            value={line.quantity}
                                            onChange={(e) => updateLine(line.key, { quantity: e.target.value })}
                                        />
                                    </label>
                                    <label className="block sm:col-span-2 min-w-0">
                                        <span className="sm:hidden block text-fd-3xs uppercase tracking-wider text-fg-3 font-display mb-1">Price £</span>
                                        <input
                                            className={`${field} w-full sm:text-right`}
                                            inputMode="decimal"
                                            value={line.unitPrice}
                                            onChange={(e) => updateLine(line.key, { unitPrice: e.target.value })}
                                        />
                                    </label>
                                    <label className="block sm:col-span-2 min-w-0">
                                        <span className="sm:hidden block text-fd-3xs uppercase tracking-wider text-fg-3 font-display mb-1">Cost £</span>
                                        <input
                                            className={`${field} w-full sm:text-right`}
                                            inputMode="decimal"
                                            value={line.costPrice}
                                            onChange={(e) => updateLine(line.key, { costPrice: e.target.value })}
                                        />
                                    </label>
                                </div>

                                <div className="flex items-center justify-between gap-3 mt-3 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-line sm:contents">
                                    <span className="sm:col-span-1 text-sm font-mono font-bold sm:font-normal text-fg sm:text-right sm:h-[42px] sm:flex sm:items-center sm:justify-end tabular-nums">
                                        <span className="sm:hidden text-fd-3xs uppercase tracking-wider text-fg-3 font-display font-normal mr-2">Total</span>
                                        £{lineTotal.toFixed(2)}
                                    </span>
                                    <button
                                        onClick={() => setLines(prev =>
                                            prev.length === 1 ? [blankLine()] : prev.filter(l => l.key !== line.key))}
                                        aria-label="Remove line"
                                        className="sm:col-span-1 inline-flex items-center justify-center gap-1.5 min-h-[44px] sm:h-[42px] sm:min-h-0 px-3 sm:px-0 rounded-lg bg-danger/10 sm:bg-transparent text-danger sm:text-fg-4 hover:bg-danger/20 sm:hover:bg-transparent sm:hover:text-danger transition-colors text-sm font-semibold"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        <span className="sm:hidden">Remove</span>
                                    </button>
                                </div>
                            </div>
                            );
                        })}

                        <button
                            onClick={() => setLines(prev => [...prev, blankLine()])}
                            className="flex items-center gap-2 text-sm font-semibold text-gold hover:underline"
                        >
                            <Plus className="w-4 h-4" /> Add another part
                        </button>

                        <p className="text-[12px] text-fg-3">
                            Cost is what you paid — it drives your margin and profit. Leave it blank if
                            you don’t have it to hand.
                        </p>
                    </section>

                    {error && (
                        <p className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-lg p-3">
                            {error}
                        </p>
                    )}
                </div>

                <footer className="p-5 border-t border-line shrink-0 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-fg-3">Sale total</span>
                        <span className="font-mono text-lg font-bold text-fg tabular-nums">
                            {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(total)}
                        </span>
                    </div>
                    <p className="text-[12px] text-fg-3 mb-3">
                        VAT is applied on the invoice using your registration settings, the same as any
                        other sale.
                    </p>
                    <button
                        onClick={handleSave}
                        disabled={!canSave}
                        className="w-full flex items-center justify-center gap-2 h-12 rounded-lg font-bold fd-btn-primary transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Recording…</> : 'Record sale & invoice'}
                    </button>
                </footer>
            </div>
        </div>,
        document.body
    );
};
